import { Quote } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../ui/accordion";
import { Eyebrow, MaskLines, Reveal } from "../motion/Reveal";
import { useSite } from "../../lib/site";

export const Testimonials = () => {
  const { content, testimonials } = useSite();
  if (!testimonials.length) return null;
  const t = content.testimonios;
  return (
    <section className="bg-paper py-24 sm:py-32" data-testid="testimonios-section">
      <div className="container-x">
        <Eyebrow tone="light">{t.eyebrow}</Eyebrow>
        <h2 className="mt-5 font-display text-4xl font-semibold tracking-[-0.035em] sm:text-5xl">{t.title}</h2>
        <div className="mt-12 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
          {testimonials.map((item, i) => (
            <Reveal key={item.id} delay={(i % 3) * 0.08} className="flex flex-col rounded-[1.75rem] border border-ink/10 bg-white p-8" data-testid={`testimonial-${i}`}>
              <Quote className="h-8 w-8 text-leaf-deep" />
              <p className="mt-6 flex-1 text-lg leading-relaxed text-ink/85">{item.quote}</p>
              <div className="mt-8 flex items-center gap-4">
                {item.image ? (
                  <img src={item.image} alt={item.name} className="h-12 w-12 rounded-full object-cover" />
                ) : (
                  <span className="grid h-12 w-12 place-items-center rounded-full bg-ink font-display text-lg font-semibold text-white">{item.name.charAt(0)}</span>
                )}
                <div>
                  <p className="font-semibold">{item.name}</p>
                  <p className="text-sm text-ink/70">{[item.role, item.company].filter(Boolean).join(" · ")}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

export const Faq = () => {
  const { content, faqs } = useSite();
  const f = content.faq;
  if (!faqs.length) return null;
  return (
    <section id="faq" className="bg-paper py-24 sm:py-32 lg:py-40" data-testid="faq-section">
      <div className="container-x grid gap-12 lg:grid-cols-12">
        <div className="lg:col-span-5">
          <div className="lg:sticky lg:top-28">
            <Eyebrow tone="light">{f.eyebrow}</Eyebrow>
            <MaskLines lines={f.title_lines} className="h-section mt-6" testId="faq-title" />
            <p className="mt-8 max-w-sm text-lg text-ink/75">{f.text}</p>
          </div>
        </div>
        <Reveal className="lg:col-span-7">
          <Accordion type="single" collapsible className="border-t border-ink/15" data-testid="faq-accordion">
            {faqs.map((item, i) => (
              <AccordionItem key={item.id} value={item.id} className="border-b border-ink/15">
                <AccordionTrigger
                  className="py-7 text-left font-display text-xl font-semibold tracking-tight hover:no-underline sm:text-2xl [&>svg]:h-6 [&>svg]:w-6"
                  data-testid={`faq-trigger-${i}`}
                >
                  {item.question}
                </AccordionTrigger>
                <AccordionContent className="pb-7 text-base leading-relaxed text-ink/80 sm:text-lg" data-testid={`faq-answer-${i}`}>
                  {item.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </Reveal>
      </div>
    </section>
  );
};
